import React, { useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Grid, Typography, ToggleButton, ToggleButtonGroup, Chip, Tooltip, LinearProgress,
} from '@mui/material';
import { TrendingUp, TrendingDown, AttachMoney, BarChart, Timeline, Phone, ReceiptLong, Router, Balance } from '@mui/icons-material';
import api from '../../services/api';

const fmtM = (n: number) => (n < 0 ? '-$' : '$') + Math.abs(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (n: number) => (Number(n) || 0).toLocaleString();
const profitColor = (n: number) => (Number(n) || 0) < 0 ? '#c62828' : '#1565c0';

export const DashboardProjection: React.FC<{ tenantId?: string; countryCode?: string }> = ({ tenantId, countryCode }) => {
  const [period, setPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams({ period: 'monthly' });
    if (tenantId) p.set('tenant_id', tenantId);
    if (countryCode) p.set('country_code', countryCode);
    api.get(`/dashboard/projection?${p.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [tenantId, countryCode]);

  const billing = data?.billing || {};
  const mtd = billing.mtd_actual || {};
  const end = billing.projected_end || {};
  const rr = billing.run_rate_daily || {};
  const cr = data?.channel_rental || {};
  const be = data?.break_even || {};
  const p = (data?.consistent && data.consistent[period]) || {};

  const cards = [
    { title: 'Revenue', value: fmtM(p.revenue), color: '#2e7d32', icon: <AttachMoney fontSize="small" /> },
    { title: 'Gross margin', value: fmtM(p.gross_profit), color: '#1565c0', icon: <TrendingUp fontSize="small" />, hint: 'Per-minute sell minus per-minute buy (before channel rental).' },
    { title: 'Channel rental', value: fmtM(p.channel_rental), color: '#b26a00', icon: <Router fontSize="small" />, hint: 'Fixed BT channel cost for this period (prorated, allocated by trunk usage).' },
    { title: 'Net profit', value: fmtM(p.profit), color: profitColor(p.profit), icon: p.profit < 0 ? <TrendingDown fontSize="small" /> : <TrendingUp fontSize="small" />, hint: 'Gross margin minus channel rental. This is the true profit.' },
    { title: 'Minutes', value: fmt(p.minutes), color: '#6a1b9a', icon: <Timeline fontSize="small" /> },
    { title: 'Calls', value: fmt(p.calls), color: '#e65100', icon: <Phone fontSize="small" /> },
  ];

  const rentalLabel = (cr.breakdown || [])
    .map((b: any) => `${b.carrier} ${b.channels}×$${b.fee}=${fmtM(b.monthly)}`).join('  +  ');

  return (
    <Box sx={{ mb: 3 }}>
      {/* ── Billing (month-to-date ACTUAL = the invoice so far), with full P&L ── */}
      <Card variant="outlined" sx={{ mb: 2, borderLeft: '4px solid #2e7d32', bgcolor: 'rgba(46,125,50,0.03)' }}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <ReceiptLong sx={{ color: '#2e7d32' }} />
            <Typography variant="subtitle1" fontWeight={700}>Billing — this month to date</Typography>
            <Chip size="small" color="success" variant="outlined" label="revenue = the invoice so far" />
            {billing.days_remaining != null && (
              <Chip size="small" variant="outlined" label={`day ${billing.day_of_month}/${billing.days_in_month} · ${billing.days_remaining} left`} />
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Billable revenue (invoice)</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#2e7d32' }}>{loading ? '…' : fmtM(mtd.revenue)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Per-minute buy cost</Typography>
              <Typography variant="h6" fontWeight={700} sx={{ color: '#c62828' }}>{loading ? '…' : fmtM(mtd.gross_profit != null ? (mtd.revenue - mtd.gross_profit) : 0)}</Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Tooltip arrow title={`Accrued ${billing.day_of_month}/${billing.days_in_month} of the month · ${fmtM(cr.per_day)}/day`}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Channel rental (accrued)</Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ color: '#b26a00' }}>{loading ? '…' : fmtM(mtd.channel_rental)}</Typography>
                </Box>
              </Tooltip>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="caption" color="text.secondary">Net profit (after rental)</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: profitColor(mtd.profit) }}>{loading ? '…' : fmtM(mtd.profit)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="caption" color="text.secondary">Projected month-end (net, at current pace)</Typography>
              <Typography variant="h5" fontWeight={800} sx={{ color: profitColor(end.profit) }}>
                {loading ? '…' : fmtM(end.profit)}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  rev {fmtM(end.revenue)} · gross {fmtM(end.gross_profit)} · rental {fmtM(end.channel_rental)}
                </Typography>
              </Typography>
            </Grid>
          </Grid>
          {!loading && cr.breakdown && (
            <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px dashed rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Router fontSize="small" sx={{ color: '#b26a00' }} />
              <Typography variant="caption" color="text.secondary">
                <b>Channel rental (BT, fixed):</b> {rentalLabel} = <b>{fmtM(cr.monthly_total)}/mo</b>
                {cr.tenant_monthly !== cr.monthly_total ? ` · this customer's share ${fmtM(cr.tenant_monthly)}/mo` : ''}
                {' · '}{fmtM(cr.per_day)}/day
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Break-even on channel rental (price locked → volume is the only lever) ── */}
      {!loading && be.minutes_needed_month != null && (
        <Card variant="outlined" sx={{ mb: 2, borderLeft: `4px solid ${be.coverage_pct >= 100 ? '#2e7d32' : '#c62828'}` }}>
          <CardContent sx={{ py: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Balance sx={{ color: be.coverage_pct >= 100 ? '#2e7d32' : '#c62828' }} />
              <Typography variant="subtitle1" fontWeight={700}>Break-even on channel rental</Typography>
              <Chip size="small" variant="outlined" label="price to customer is fixed — volume is the only lever" />
              <Tooltip title={`Forward-looking: gross margin projected for a full month at the current pace (last 3 complete days) vs the ${fmtM(be.monthly_rental)} monthly rental.`}>
                <Chip size="small" color={be.coverage_pct >= 100 ? 'success' : 'error'}
                  label={`Run-rate: ${be.coverage_pct}% of rental covered`} sx={{ fontWeight: 700 }} />
              </Tooltip>
              {be.mtd_coverage_pct != null && (
                <Tooltip title={`This month's actuals so far: ${fmtM(be.mtd_gross_margin)} gross margin earned vs ${fmtM(be.mtd_rental_accrued)} rental accrued to date.`}>
                  <Chip size="small" variant="outlined" color={be.mtd_coverage_pct >= 100 ? 'success' : 'warning'}
                    label={`MTD: ${be.mtd_coverage_pct}% of accrued rental`} sx={{ fontWeight: 700 }} />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <LinearProgress variant="determinate" value={Math.min(100, be.coverage_pct || 0)}
                sx={{ height: 10, borderRadius: 5,
                  '& .MuiLinearProgress-bar': { bgcolor: be.coverage_pct >= 100 ? '#2e7d32' : '#c62828' } }} />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Minutes needed / month</Typography>
                <Typography variant="h6" fontWeight={800}>{fmt(be.minutes_needed_month)}</Typography>
                <Typography variant="caption" color="text.secondary">~{fmt(be.minutes_needed_day)}/day</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Minutes now (run-rate)</Typography>
                <Typography variant="h6" fontWeight={800}>{fmt(be.minutes_current_month)}</Typography>
                <Typography variant="caption" color="text.secondary">~{fmt(be.minutes_current_day)}/day</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Shortfall / month</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: '#c62828' }}>{fmt(be.minutes_shortfall_month)}</Typography>
                <Typography variant="caption" color="text.secondary">more minutes needed</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Volume required</Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color: be.volume_multiple > 1 ? '#c62828' : '#2e7d32' }}>
                  {be.volume_multiple}× current
                </Typography>
                <Typography variant="caption" color="text.secondary">to reach break-even</Typography>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              At the locked margin of ${be.margin_per_min}/min, covering the {fmtM(be.monthly_rental)}/mo channel rental needs
              {' '}<b>{fmt(be.minutes_needed_month)} min/mo</b> ({fmtM(be.revenue_needed_month)} revenue) — currently ~{fmt(be.minutes_current_month)} min/mo.
              {be.coverage_pct >= 100
                ? ' ✅ Current volume covers the rental.'
                : ` ⚠️ Need ${be.volume_multiple}× the current volume (or a lower channel cost) to break even.`}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ── Run-rate projection (consistent daily/weekly/monthly, net of rental) ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight={600}>Projection</Typography>
        <ToggleButtonGroup size="small" exclusive value={period} onChange={(_, v) => v && setPeriod(v)}>
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="weekly">Weekly</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
        <Chip size="small" variant="outlined"
          label={period === 'monthly'
            ? 'month-to-date actual + rest of month at current run-rate'
            : `current run-rate${rr.basis_days ? ` (last ${rr.basis_days} complete days)` : ''} → ${period === 'daily' ? '1 day' : '1 week'}`} />
      </Box>
      <Grid container spacing={2}>
        {cards.map((c) => (
          <Grid item xs={6} sm={4} md={2} key={c.title}>
            <Tooltip arrow title={c.hint || ''} disableHoverListener={!c.hint}>
              <Card variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: c.color, mb: 0.5 }}>
                    {c.icon}
                    <Typography variant="caption" color="text.secondary">{c.title}</Typography>
                  </Box>
                  <Typography variant="h5" fontWeight={700} sx={{ color: c.color }}>{loading ? '…' : c.value}</Typography>
                </CardContent>
              </Card>
            </Tooltip>
          </Grid>
        ))}
      </Grid>
      {data && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Run-rate basis — last {rr.basis_days ?? 3} complete days: {fmtM(rr.revenue)}/day revenue, {fmt(rr.minutes)} min/day.
          <b> Net profit = gross margin − channel rental.</b> The <b>Billing revenue</b> above is exact and matches the invoice.
          {p.profit < 0 ? ' ⚠️ Net profit is negative — per-minute margin is not covering the fixed channel rental.' : ''}
        </Typography>
      )}
    </Box>
  );
};
